import { keymap } from 'prosemirror-keymap';
import {
  toggleMark,
  setBlockType,
  chainCommands,
  liftEmptyBlock,
  joinBackward,
  splitBlock,
  deleteSelection,
} from 'prosemirror-commands';
import {
  inputRules,
  textblockTypeInputRule,
  wrappingInputRule,
  InputRule,
} from 'prosemirror-inputrules';
import {
  splitListItem,
  liftListItem,
  sinkListItem,
} from 'prosemirror-schema-list';
import { goToNextCell, addRowAfter } from 'prosemirror-tables';
import { schema } from '../schema';
import { Plugin } from 'prosemirror-state';
import { findMultiplier } from './multiplier-plugin';
import { insertTableCommand } from '../toolbar';
import { convertTaskToParagraph, handleTaskEnter } from '../commands/task-commands';

// Create number node input rule (*=123 )
function numberNodeRule() {
  return new InputRule(/\*=(\d+(?:\.\d+)?)\s$/, (state, match, start, end) => {
    const multipliedNumber = parseFloat(match[1]);
    if (isNaN(multipliedNumber)) return null;

    const multiplier = findMultiplier(state.doc);
    const base = multipliedNumber / multiplier;
    const numberNode = schema.nodes.numberNode.create({ base });
    const tr = state.tr.replaceRangeWith(start, end, numberNode);
    return tr;
  });
}

// Create tag mark input rule (#word)
function tagInputRule() {
  // Match # followed by any character except whitespace and #
  // The # is removed, only the text after it becomes the tag
  return new InputRule(/#([^\s#])$/, (state, match, start, end) => {
    const tagMark = schema.marks.tag.create();
    return state.tr
      .delete(start, end)
      .insertText(match[1])
      .addMark(start, start + match[1].length, tagMark)
      .addStoredMark(tagMark);
  });
}

export function createInputRulesPlugin(): Plugin {
  return inputRules({
    rules: [
      // Heading hierarchy is enforced by heading-plugin's filterTransaction
      // Only ## and ### create headings (# is reserved for tags)
      textblockTypeInputRule(/^(#{2,3})\s$/, schema.nodes.heading, match => ({ level: match[1].length })),
      textblockTypeInputRule(/^-?\s*\[\s?\]\s$/, schema.nodes.task, { done: false, active: false }),
      numberNodeRule(),
      tagInputRule(),
      // List input rules
      wrappingInputRule(/^([-*])\s$/, schema.nodes.bulletList),
      wrappingInputRule(
        /^(\d+)\.\s$/,
        schema.nodes.orderedList,
        match => ({ order: parseInt(match[1], 10) }),
        (match, node) => node.childCount + node.attrs.order === parseInt(match[1], 10)
      ),
    ],
  });
}

export function createKeymapPlugin(): Plugin {
  return keymap({
    'Mod-b': toggleMark(schema.marks.strong),
    'Mod-i': toggleMark(schema.marks.em),
    // Heading hierarchy is enforced by heading-plugin's filterTransaction
    'Mod-1': setBlockType(schema.nodes.heading, { level: 1 }),
    'Mod-2': setBlockType(schema.nodes.heading, { level: 2 }),
    'Mod-3': setBlockType(schema.nodes.heading, { level: 3 }),
    'Mod-0': setBlockType(schema.nodes.paragraph),
    'Mod-m': (state, dispatch) => {
      // Convert selected text to number node with that text as the multiplied number
      const { from, to } = state.selection;
      const selectedText = state.doc.textBetween(from, to);
      const parsedNumber = parseFloat(selectedText);
      const multiplier = findMultiplier(state.doc);
      const base = !isNaN(parsedNumber) ? parsedNumber / multiplier : 1;
      const numberNode = schema.nodes.numberNode.create({ base });

      if (dispatch) {
        const tr = state.tr.replaceSelectionWith(numberNode);
        dispatch(tr);
      }
      return true;
    },
    Space: (state, dispatch) => {
      // Exit multiplier or tag mark on space
      const { $from } = state.selection;
      if (schema.marks.multiplier.isInSet($from.marks()) || schema.marks.tag.isInSet($from.marks())) {
        dispatch?.(state.tr.setStoredMarks([]).insertText(' '));
        return true;
      }
      return false;
    },
    Enter: chainCommands(
      // Split list item on Enter
      splitListItem(schema.nodes.listItem),
      // If we're in a task, handle Enter (split or convert to paragraph)
      handleTaskEnter,
      // If we're in a heading, split and create a paragraph
      (state, dispatch) => {
        const { $from } = state.selection;
        if ($from.parent.type.name === 'heading') {
          dispatch?.(state.tr.split($from.pos, 1, [{ type: schema.nodes.paragraph }]));
          return true;
        }
        return false;
      },
      // Default behavior using splitBlock
      splitBlock,
    ),
    Backspace: chainCommands(
      // Delete selection first (handles tables and other block selections)
      deleteSelection,
      // If we're at the start of a task, convert to paragraph (keeps content)
      convertTaskToParagraph,
      // If we're at the start of an empty heading, convert to paragraph
      (state, dispatch) => {
        const { $from } = state.selection;
        const parent = $from.parent;

        if (
          parent.type.name === 'heading' &&
          $from.parentOffset === 0 &&
          parent.content.size === 0
        ) {
          dispatch?.(state.tr.setNodeMarkup($from.before(), schema.nodes.paragraph));
          return true;
        }
        return false;
      },
      // Lift list item only when at start of empty paragraph inside list item
      (state, dispatch) => {
        const { $from } = state.selection;
        // Check if we're at the start of an empty paragraph
        if ($from.parent.type === schema.nodes.paragraph &&
            $from.parentOffset === 0 &&
            $from.parent.content.size === 0) {
          // Check if parent is listItem
          const listItemDepth = $from.depth - 1;
          if (listItemDepth > 0 && $from.node(listItemDepth).type === schema.nodes.listItem) {
            return liftListItem(schema.nodes.listItem)(state, dispatch);
          }
        }
        return false;
      },
      liftEmptyBlock,
      joinBackward,
    ),
    Delete: deleteSelection,
    // Tab: table navigation, add row at end, or list nesting
    Tab: chainCommands(
      goToNextCell(1),
      // If goToNextCell failed and we're in a table, add a new row and move to it
      (state, dispatch, view) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type === schema.nodes.table) {
            // Add row after current row, then navigate to first cell
            if (addRowAfter(state, dispatch) && view) {
              // After adding row, goToNextCell will now succeed
              goToNextCell(1)(view.state, view.dispatch);
            }
            return true;
          }
        }
        return false;
      },
      sinkListItem(schema.nodes.listItem)
    ),
    'Shift-Tab': chainCommands(goToNextCell(-1), liftListItem(schema.nodes.listItem)),
    // Insert table
    'Mod-Shift-t': insertTableCommand,
  });
}
